/**
 * Marketing consent — the copy, and the record written against an account.
 *
 * WHY A MODULE FOR ONE SENTENCE. The consent record stores the exact wording the
 * person was shown, not just a boolean, because a boolean says they agreed and
 * not what to. Copy changes over time; the record of what a given person agreed
 * to must not change with it. That only works if there is exactly one definition
 * of the current wording, and everything that writes consent reads it from here.
 *
 * ⚠ CHANGING `CONSENT_TEXT` IS NOT A COPY EDIT. New consents are recorded
 * against the new wording; existing records keep the sentence they were given.
 * That is the intended behaviour and the whole point of storing the text. Bump
 * `CONSENT_VERSION` when the meaning changes, so the two can be told apart
 * without string-matching prose.
 *
 * ⚠ WE DO NOT ASK AT SIGNUP, AND THAT IS DELIBERATE. The first attempt put a
 * checkbox on the register screen. It cannot work: `login()` in login.vue is
 * bound to the Google button on BOTH the sign-in and register views and calls
 * createUser unconditionally, so a brand-new account is routinely created from
 * the SIGN-IN screen — where a register-screen checkbox is not merely unseen,
 * it is not rendered. 46% of accounts (3,357 of 7,325) are created with Google.
 *
 * The ask therefore happens ONCE, on the dashboard, after the account exists,
 * where every signup route has landed. See `dashboard.vue`.
 *
 * Settled with Josh 8 Sept 2026:
 *  - Leads with "updates", not "product news" — informational, not promotional.
 *  - No mention of Terms or Privacy. The moment consent copy references them the
 *    two become arguably one act, which is the bundling that invalidates it.
 *  - A question with two answers, not an optional checkbox. A checkbox is a
 *    thing you can ignore; "Yes" / "No thanks" gets answered, and records a real
 *    decision either way — which is what lets it be asked exactly once.
 */

export const CONSENT_VERSION = 1;

/**
 * ⛔ NEW ACCOUNTS ONLY. Accounts created before this moment are never prompted.
 *
 * Josh, 9 Sept 2026: ask new signups, do not put the banner in front of all
 * 7,322 existing users at once. Without this the banner would meet essentially
 * every customer on their next login, because none of them has a consent
 * record — intended behaviour for capture, but the loudest possible change and
 * not the one he wants.
 *
 * ⚠ Compared against FIREBASE AUTH's `metadata.creationTime`, not a Firestore
 * field, and that is deliberate: `users/{uid}` has NO created-at. (Nor an
 * updated-at, nor a cancellation date — the same gap step 37 turns on.) Auth has
 * always carried one, so this needs no backfill, no new field and no migration.
 *
 * ⚠ SET THIS TO THE ACTUAL PRODUCTION DEPLOY MOMENT before the release. Too
 * early and it prompts existing users, which is the thing being avoided; too
 * late and nobody is ever asked and the feature looks broken while working
 * perfectly. Currently: just after the Sunday 13 Sept night deploy, Pacific.
 */
export const CONSENT_PROMPT_FROM = '2026-09-14T04:00:00.000Z';

/**
 * Was this account created after we started asking?
 *
 * Defensive on purpose: if Auth gives us no creation time, or an unparseable
 * one, this returns FALSE and the account is treated as pre-existing. The safe
 * failure is not asking someone, never accidentally prompting the whole base.
 */
export function isPromptableAccount(authUser) {
    const raw = authUser && authUser.metadata && authUser.metadata.creationTime;
    if (!raw) return false;
    const created = Date.parse(raw);
    const from = Date.parse(CONSENT_PROMPT_FROM);
    if (isNaN(created) || isNaN(from)) return false;
    return created >= from;
}

/**
 * Does this account hold, or has it ever held, an institutional seat?
 *
 * ⭐ WHY AFFILIATES ARE ASKED TOO — Josh, 19 Sept 2026: "One option might be to
 * put that banner that asks for permission (that we currently put for new users
 * only) on affiliate accounts that haven't consented."
 *
 * The reason it matters is that the affiliate consent gap IS the Zapier bug in
 * the data. Zapier only ever watched Stripe, and an affiliate never transacts,
 * so they were never in Mailchimp, so they were not in the 13 Sept backfill, so
 * NOBODY EVER ASKED THEM. Measured 19 Sept: anyone who ever transacted is ~70%
 * reachable; affiliates are 2.8% — 22 consented out of 792. This puts the ask in
 * front of the 753 who have never seen it.
 *
 * ⚠ DELIBERATELY A LEAKY PROXY, and only safe because of what it is used for.
 * `affiliateInstitution` persists after an admin drops someone from their roster
 * — 573 accounts carry all-false rosters, and 24 stale pointers were cleared on
 * 11 Sept. For ASKING ABOUT CONSENT that is exactly right: Josh wants people who
 * "are or have been" affiliates. ⛔ Never reuse this predicate for entitlement,
 * which must check BOTH sides of the link (see entitlement-reconcile.js).
 *
 * @param {object} user  the `users/{uid}` document
 */
export function isSeatHolderAccount(user) {
    return Boolean(user && user.affiliateInstitution);
}

/**
 * Should the dashboard ask this person? Two independent reasons, one answer, so
 * the view never re-implements the rule.
 *
 * ⚠ The caller must still check that `marketing_consent` is absent. Absence is
 * the trigger and `false` is respected forever, which is what makes this a
 * once-only prompt for both branches alike.
 *
 * @param {object} authUser  Firebase Auth user (for creationTime)
 * @param {object} user      the `users/{uid}` document
 * @returns {string|null}  the consent SOURCE to record, or null for "do not ask"
 */
export function consentPromptSource(authUser, user) {
    // New accounts first: someone who signed up after the cutoff AND happens to
    // hold a seat is a new signup, and recording them as an affiliate prompt
    // would overstate what that branch achieved.
    if (isPromptableAccount(authUser)) return 'first_run';
    if (isSeatHolderAccount(user)) return 'affiliate_prompt';
    return null;
}

export const CONSENT_TEXT =
    'Email me updates about Cuelist and occasional product news.';

export const CONSENT_SUBTEXT =
    'You can turn this off any time in your account settings.';

/**
 * The fields written when consent is given or withdrawn.
 *
 * ⚠ ABSENCE IS MEANINGFUL. An account with no `marketing_consent` field has
 * never been asked; one with `false` has answered no. The dashboard prompt keys
 * on that distinction, so never seed the field with a default.
 *
 * @param {boolean} granted
 * @param {string} source  'first_run' | 'account_settings' | 'signup' |
 *   'affiliate_prompt' — kept distinct so each surface's yield is measurable on
 *   its own. The dashboard banner has produced 34 consents in six days, signup 3
 *   and account settings 3; the affiliate branch needs to be counted separately
 *   or it disappears into the first of those.
 */
export function consentFields(granted, source) {
    return {
        marketing_consent: !!granted,
        marketing_consent_at: new Date().toISOString(),
        // Stored verbatim so the answer survives every future copy change.
        marketing_consent_text: CONSENT_TEXT,
        marketing_consent_version: CONSENT_VERSION,
        marketing_consent_source: source
    };
}

export default {
    CONSENT_TEXT,
    CONSENT_SUBTEXT,
    CONSENT_VERSION,
    CONSENT_PROMPT_FROM,
    consentFields,
    isPromptableAccount,
    isSeatHolderAccount,
    consentPromptSource
};


// WEBPACK FOOTER //
// ./src/lib/marketingConsent.js